import { useCallback, useMemo, useRef } from 'react';

import { CustomerRegistrationForm, Address, NoharaRepresentative } from '../../types/company.types';
import { validateCustomerRegistrationForm } from '../../utils/validations';
import {
  useCustomerFormData,
  useCustomerValidation,
  useCustomerActions,
  useCustomerTouchedFields,
} from '../../stores/company-registration.store';
import { useMarkRequiredFieldsTouched } from '../common/use-mark-required-fields-touched';

export const useCustomerRegistrationForm = (
  onSubmit?: (data: CustomerRegistrationForm) => Promise<void>
) => {
  // Get data and actions from global store
  const formData = useCustomerFormData();
  const validationState = useCustomerValidation();
  const touchedFields = useCustomerTouchedFields();
  const actions = useCustomerActions();
  const { updateForm, setValidation, resetForm: resetGlobalForm, setTouchedFields } = actions;

  // Use refs to avoid dependencies in useCallback
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Mark all required fields as touched on form load to show required errors immediately
  // Customer form has many required fields, so we mark fields that have validation errors
  const initialValidation = useMemo(() => validateCustomerRegistrationForm(formData), [formData]);
  useMarkRequiredFieldsTouched({
    touchedFields,
    setTouchedFields,
    representatives: formData.representatives,
    fieldsToMark: initialValidation.errors,
    useValidationErrors: true,
  });

  // Validate form whenever formData changes
  const validation = useMemo(() => {
    const result = validateCustomerRegistrationForm(formData);
    return result;
  }, [formData, touchedFields]);

  // Update field value and mark as touched
  const updateField = useCallback(
    <K extends keyof CustomerRegistrationForm>(field: K, value: CustomerRegistrationForm[K]) => {
      updateForm({ [field]: value });
      // Mark field as touched for immediate validation display
      const next = new Set(touchedFields);
      next.add(String(field));
      setTouchedFields(next);
    },
    [updateForm, setTouchedFields, touchedFields]
  );

  // Update nested address field and mark as touched
  const updateAddressField = useCallback(
    (addressType: 'currentAddress' | 'headOfficeAddress', field: keyof Address, value: string) => {
      updateForm({
        [addressType]: {
          ...formDataRef.current[addressType],
          [field]: value,
        },
      });
      // Mark nested field as touched
      const next = new Set(touchedFields);
      next.add(`${addressType}.${String(field)}`);
      setTouchedFields(next);
    },
    [updateForm, setTouchedFields, touchedFields]
  );

  // Auto-pad postal codes on blur
  const handleAddressFieldBlur = useCallback(
    (addressType: 'currentAddress' | 'headOfficeAddress', field: keyof Address, value: string) => {
      if (field === 'postalCode1' && value && /^\d{1,3}$/.test(value)) {
        const paddedValue = value.padStart(3, '0');
        updateForm({
          [addressType]: {
            ...formDataRef.current[addressType],
            [field]: paddedValue,
          },
        });
      } else if (field === 'postalCode2' && value && /^\d{1,4}$/.test(value)) {
        const paddedValue = value.padStart(4, '0');
        updateForm({
          [addressType]: {
            ...formDataRef.current[addressType],
            [field]: paddedValue,
          },
        });
      }
    },
    [updateForm]
  );

  // Copy address from current to head office (excluding phone and fax)
  const copyCurrentToHeadOffice = useCallback(() => {
    const { postalCode1, postalCode2, address1, address2, address3 } =
      formDataRef.current.currentAddress;
    updateForm({
      headOfficeAddress: { postalCode1, postalCode2, address1, address2, address3 },
    });

    // Mark all head office address fields as touched to show validation immediately
    const next = new Set(touchedFields);
    for (const field of ['postalCode1', 'postalCode2', 'address1', 'address2', 'address3']) {
      next.add(`headOfficeAddress.${field}`);
    }
    setTouchedFields(next);
  }, [updateForm, setTouchedFields, touchedFields]);

  // Representatives management
  const addRepresentative = useCallback(() => {
    if (formDataRef.current.representatives.length >= 10) {
      return; // Max 10 representatives
    }

    const newRepresentative: NoharaRepresentative = {
      department: '',
      salesPerson: {
        id: '',
        lastName: '',
        firstName: '',
        code: '',
      },
    };
    const newIndex = formDataRef.current.representatives.length;

    updateForm({
      representatives: [...formDataRef.current.representatives, newRepresentative],
    });

    // Mark new representative fields as touched to trigger validation immediately
    const newTouched = new Set(touchedFields);
    newTouched.add(`representatives.${newIndex}.department`);
    newTouched.add(`representatives.${newIndex}.salesPerson`);
    setTouchedFields(newTouched);
  }, [updateForm, touchedFields, setTouchedFields]);

  const removeRepresentative = useCallback(
    (id: string) => {
      const repIndex = Number.parseInt(id);
      if (
        Number.isNaN(repIndex) ||
        repIndex < 0 ||
        repIndex >= formDataRef.current.representatives.length
      )
        return;

      updateForm({
        representatives: formDataRef.current.representatives.filter((_, i) => i !== repIndex),
      });

      // Clean up touched fields for removed representative and re-index remaining ones
      const newTouched = new Set<string>();
      for (const field of touchedFields) {
        // Skip fields for removed representative
        if (
          field === `representatives.${repIndex}.department` ||
          field === `representatives.${repIndex}.salesPerson`
        ) {
          continue;
        }
        // Re-index fields after removed representative
        const match = field.match(/^representatives\.(\d+)\.(.+)$/);
        if (match) {
          const fieldIndex = Number.parseInt(match[1]);
          const fieldName = match[2];
          if (fieldIndex > repIndex) {
            // Decrement index for fields after removed one
            newTouched.add(`representatives.${fieldIndex - 1}.${fieldName}`);
          } else {
            newTouched.add(field);
          }
        } else {
          newTouched.add(field);
        }
      }
      setTouchedFields(newTouched);
    },
    [updateForm, touchedFields, setTouchedFields]
  );

  const updateRepresentative = useCallback(
    (id: string, field: keyof NoharaRepresentative, value: string) => {
      const repIndex = Number.parseInt(id);
      if (
        Number.isNaN(repIndex) ||
        repIndex < 0 ||
        repIndex >= formDataRef.current.representatives.length
      )
        return;

      const updatedRepresentatives = formDataRef.current.representatives.map((rep, i) =>
        i === repIndex ? { ...rep, [field]: value } : rep
      );

      // When department changes, reset sales person
      if (field === 'department') {
        const finalRepresentatives = updatedRepresentatives.map((rep, i) =>
          i === repIndex
            ? { ...rep, salesPerson: { id: '', lastName: '', firstName: '', code: '' } }
            : rep
        );
        updateForm({ representatives: finalRepresentatives });
      } else {
        updateForm({ representatives: updatedRepresentatives });
      }

      // Mark representative field as touched
      const next = new Set(touchedFields);
      next.add(`representatives.${repIndex}.${String(field)}`);
      setTouchedFields(next);
    },
    [updateForm, setTouchedFields, touchedFields]
  );

  // Form submission
  const handleSubmit = useCallback(async () => {
    // Mark all fields as touched to show validation errors
    const allFieldNames = new Set([
      'companyType',
      'galiCustomerCode',
      'officialName',
      'abbreviatedName',
      'branchDepartment',
      'customerNameKatakana',
      'customerCategory',
      'cementCommon',
      'currentAddress.postalCode1',
      'currentAddress.postalCode2',
      'currentAddress.address1',
      'currentAddress.address2',
      'currentAddress.address3',
      'currentAddress.phone',
      'currentAddress.fax',
      'headOfficeAddress.postalCode1',
      'headOfficeAddress.postalCode2',
      'headOfficeAddress.address1',
      'headOfficeAddress.address2',
      'headOfficeAddress.address3',
      'headOfficeAddress.phone',
      'headOfficeAddress.fax',
      ...formData.representatives.flatMap((_, index) => [
        `representatives.${index}.department`,
        `representatives.${index}.salesPerson`,
      ]),
    ]);
    setTouchedFields(allFieldNames);

    setValidation({ ...validationState, isSubmitting: true });

    try {
      const validationResult = validateCustomerRegistrationForm(formData);

      if (!validationResult.isValid) {
        setValidation({
          errors: validationResult.errors,
          isValid: false,
          isSubmitting: false,
        });
        return false;
      }

      if (onSubmit) {
        await onSubmit(formData);
      }

      setValidation({
        errors: [],
        isValid: true,
        isSubmitting: false,
      });

      return true;
    } catch {
      setValidation({
        ...validationState,
        isSubmitting: false,
        errors: [{ field: 'general', message: '登録に失敗しました。' }],
      });
      return false;
    }
  }, [formData, onSubmit, setTouchedFields, setValidation, validationState]);

  // Form reset
  const resetForm = useCallback(() => {
    resetGlobalForm();
    setValidation({
      errors: [],
      isValid: true,
      isSubmitting: false,
    });
    setTouchedFields(new Set());
  }, [resetGlobalForm, setValidation, setTouchedFields]);

  // Helper functions - show validation for touched fields or after submit attempt
  const getFieldError = useCallback(
    (fieldName: string) => {
      // Show error if field is touched, form has been submitted, or currently submitting
      const errorMessage = validation.errors.find((error) => error.field === fieldName)?.message;
      return errorMessage;
    },
    [validation.errors]
  );

  const isFieldValid = useCallback(
    (fieldName: string) => {
      // Show error if field is touched, form has been submitted, or currently submitting
      return !validation.errors.some((error) => error.field === fieldName);
    },
    [validation.errors]
  );

  // Sales person options (filtered by department)
  const getSalesPersonOptions = useCallback((departmentId: string) => {
    // Mock data - in real app, this would come from API
    const mockSalesPersons = [
      { id: 'sp1', name: '田中太郎', departmentId: '105000' },
      { id: 'sp2', name: '佐藤花子', departmentId: '105000' },
      { id: 'sp3', name: '鈴木一郎', departmentId: '105000' },
      { id: 'sp4', name: '高橋美香', departmentId: '105000' },
    ];

    return mockSalesPersons.filter((person) => person.departmentId === departmentId);
  }, []);

  return {
    // State
    formData,
    validationState,
    validation,

    // Actions
    updateField,
    updateAddressField,
    copyCurrentToHeadOffice,

    // Blur handlers for auto-padding
    handleAddressFieldBlur,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,
    resetForm,

    // Helpers
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,

    // Computed values
    canAddRepresentative: formData.representatives.length < 10,
    isSubmitDisabled: !validation.isValid || validationState.isSubmitting,
  };
};
